import { GrowthBook } from '@growthbook/growthbook';

/**
 * Binder Content Element (BCE) System
 * 
 * Modular system for TYPO3 content elements with automatic initialization
 * and multi-language support. Now with GrowthBook A/B testing integration.
 * 
 * @class BCE
 */
class BCE {
    constructor() {
        this.modules = {};
        this.initialized = false;
        this.autoScanEnabled = true;
        this.growthBook = null;
        this.growthBookEnabled = false;
    }

    /**
     * Register a module with the BCE system
     * 
     * @param {string} name - Module name (e.g., 'slider', 'gallery')
     * @param {Object} module - Module object with init/create methods
     */
    register(name, module) {
        this.modules[name] = module;
        this[name] = module;
    }

    /**
     * Initialize GrowthBook for A/B testing
     * @param {Object} config - GrowthBook configuration
     * @param {string} config.apiHost - GrowthBook API host
     * @param {string} config.clientKey - SDK client key
     * @param {Object} config.attributes - User attributes for targeting
     */
    async initGrowthBook(config = {}) {
        const defaultConfig = {
            apiHost: "https://api.growthbook.binder-world.com",
            clientKey: "sdk-aRsp3Hw7QYQQEq",
            enableDevMode: true, // Für Chrome Plugin Detection
            attributes: {
                id: this.generateUserId() // Nur die essenzielle User-ID
            },
            trackingCallback: (experiment, result) => {
                console.log('BCE A/B Test:', { experiment: experiment.key, variant: result.key });
                // Hier könnt ihr eure Analytics integrieren
            }
        };

        const finalConfig = { ...defaultConfig, ...config };
        
        try {
            this.growthBook = new GrowthBook(finalConfig);
            await this.growthBook.init();
            this.growthBookEnabled = true;
            console.log('BCE: GrowthBook initialized successfully');
        } catch (error) {
            console.error('BCE: GrowthBook initialization failed:', error);
            this.growthBookEnabled = false;
        }
    }

    /**
     * Initialize the BCE system
     * Scans for elements with bce_ classes and initializes modules
     */
    init() {
        if (this.initialized) return;
        
        // Auto-scan für bce_ Klassen
        if (this.autoScanEnabled) {
            this.scanAndInitialize();
        }
        
        
        this.initialized = true;
    }

    scanAndInitialize() {
        // Single query for all BCE-related classes - major performance improvement
        const allElements = document.querySelectorAll('[class*="bce_"], [class*="bce-"], [class*="binder_"], [class*="binder-"]');
        
        // Early exit for 98% of pages without BCE elements
        if (!allElements.length) {
            return;
        }
        
        // Process all found elements with optimized single loop
        allElements.forEach(element => {
            // Skip if already processed (prevent duplicate initialization)
            if (element.hasAttribute('data-bce-processed')) {
                return;
            }
            
            // Find first matching BCE class (no need to process all classes)
            for (const className of element.classList) {
                const moduleInfo = this.getModuleFromClassName(className);
                if (moduleInfo) {
                    if (moduleInfo.isLegacy) {
                        console.warn(`BCE: Legacy class "${className}" found. Please migrate to "bce_${moduleInfo.moduleName}"`);
                    }
                    
                    // Mark as processed before initialization to prevent duplicates
                    element.setAttribute('data-bce-processed', 'true');
                    this.initializeModule(moduleInfo.moduleName, element);
                    break; // Stop after first match - one module per element
                }
            }
        });
    }

    initializeModule(moduleName, element) {
        const module = this.modules[moduleName];
        
        if (!module) {
            console.warn(`BCE: Module "${moduleName}" not found. Available modules:`, Object.keys(this.modules));
            return;
        }

        // Transform legacy classes to bce_ format for CSS compatibility
        this.transformLegacyClasses(element, moduleName);

        if (typeof module.create === 'function') {
            try {
                module.create(element);
                // Removed verbose logging - only log errors now
            } catch (error) {
                console.error(`BCE: Error initializing module "${moduleName}":`, error);
                element.classList.add('bce-error');
            }
        } else if (typeof module.init === 'function') {
            try {
                module.init(`.bce_${moduleName}`);
                // Removed verbose logging - only log errors now
            } catch (error) {
                console.error(`BCE: Error initializing module "${moduleName}":`, error);
            }
        } else {
            console.warn(`BCE: Module "${moduleName}" has no init or create method`);
        }
    }

    // Manuelle Initialisierung eines Moduls
    initModule(moduleName, selector, options = {}) {
        const module = this.modules[moduleName];
        
        if (!module) {
            console.error(`BCE: Module "${moduleName}" not found`);
            return false;
        }

        if (typeof module.init === 'function') {
            module.init(selector, options);
            return true;
        } else if (typeof module.create === 'function') {
            const elements = document.querySelectorAll(selector);
            elements.forEach(element => module.create(element, options));
            return true;
        }

        console.error(`BCE: Module "${moduleName}" has no init or create method`);
        return false;
    }

    // Re-scan für dynamisch hinzugefügte Elemente
    rescan() {
        if (!this.initialized) {
            console.warn('BCE: System not initialized yet');
            return;
        }
        
        this.scanAndInitialize();
    }

    // Deaktiviere Auto-Scan (für manuelle Kontrolle)
    disableAutoScan() {
        this.autoScanEnabled = false;
    }

    // Aktiviere Auto-Scan
    enableAutoScan() {
        this.autoScanEnabled = true;
    }

    ready(callback) {
        if (document.readyState === 'loading') {
            document.addEventListener('DOMContentLoaded', callback);
        } else {
            callback();
        }
    }

    // Debugging Hilfsmethoden
    getModules() {
        return Object.keys(this.modules);
    }

    getModuleInstance(moduleName) {
        return this.modules[moduleName];
    }

    // ===== GROWTHBOOK A/B TESTING METHODS =====

    /**
     * Generate or retrieve stable user ID for A/B testing
     * @returns {string} User ID for bucketing
     */
    generateUserId() {
        try {
            let userId = localStorage.getItem('gb-user-id');
            if (!userId) {
                // Generate stable anonymous ID
                userId = 'anon_' + Math.random().toString(36).substr(2, 9) + '_' + Date.now();
                localStorage.setItem('gb-user-id', userId);
            }
            return userId;
        } catch (e) {
            // Fallback if localStorage unavailable
            return 'anon_' + Math.random().toString(36).substr(2, 9);
        }
    }

    /**
     * Check if a feature flag is enabled
     * @param {string} feature - Feature flag name
     * @param {boolean} defaultValue - Default value if GrowthBook unavailable
     * @returns {boolean}
     */
    isFeatureOn(feature, defaultValue = false) {
        if (!this.growthBookEnabled || !this.growthBook) {
            return defaultValue;
        }
        return this.growthBook.isOn(feature);
    }

    /**
     * Get a feature value with fallback
     * @param {string} feature - Feature flag name  
     * @param {any} defaultValue - Default value
     * @returns {any}
     */
    getFeatureValue(feature, defaultValue = null) {
        if (!this.growthBookEnabled || !this.growthBook) {
            return defaultValue;
        }
        return this.growthBook.getFeatureValue(feature, defaultValue);
    }

    /**
     * Update user attributes for targeting
     * @param {Object} attributes - User attributes
     */
    updateUserAttributes(attributes) {
        if (this.growthBookEnabled && this.growthBook) {
            this.growthBook.setAttributes(attributes);
        }
    }

    /**
     * Conditional module initialization based on feature flags
     * @param {string} feature - Feature flag name
     * @param {function} callback - Callback to execute if feature is on
     * @param {function} fallback - Optional fallback if feature is off
     */
    withFeature(feature, callback, fallback = null) {
        if (this.isFeatureOn(feature)) {
            callback();
        } else if (fallback) {
            fallback();
        }
    }

    /**
     * Add CSS classes based on feature flags
     * @param {HTMLElement} element - Target element
     * @param {string} feature - Feature flag name
     * @param {string} activeClass - Class to add when feature is on
     * @param {string} inactiveClass - Class to add when feature is off
     */
    applyFeatureClass(element, feature, activeClass, inactiveClass = null) {
        if (this.isFeatureOn(feature)) {
            element.classList.add(activeClass);
            if (inactiveClass) element.classList.remove(inactiveClass);
        } else if (inactiveClass) {
            element.classList.add(inactiveClass);
            element.classList.remove(activeClass);
        }
    }

    /**
     * Transform legacy class names to bce_ format for CSS compatibility
     * @param {HTMLElement} element - The element to transform
     * @param {string} moduleName - The module name
     */
    transformLegacyClasses(element, moduleName) {
        const legacyClasses = [
            `bce-${moduleName}`,
            `binder_${moduleName}`,
            `binder-${moduleName}`
        ];
        
        const targetClass = `bce_${moduleName}`;
        
        // Check if element has any legacy classes and transform them
        legacyClasses.forEach(legacyClass => {
            if (element.classList.contains(legacyClass)) {
                element.classList.remove(legacyClass);
                if (!element.classList.contains(targetClass)) {
                    element.classList.add(targetClass);
                }
            }
        });
    }

    /**
     * Extract module information from class name
     * @param {string} className - The CSS class name
     * @returns {Object|null} Module info with name and legacy status
     */
    getModuleFromClassName(className) {
        if (className.startsWith('bce_')) {
            return {
                moduleName: className.replace('bce_', ''),
                isLegacy: false
            };
        }
        if (className.startsWith('bce-')) {
            return {
                moduleName: className.replace('bce-', ''),
                isLegacy: true
            };
        }
        if (className.startsWith('binder_')) {
            return {
                moduleName: className.replace('binder_', ''),
                isLegacy: true
            };
        }
        if (className.startsWith('binder-')) {
            return {
                moduleName: className.replace('binder-', ''),
                isLegacy: true
            };
        }
        return null;
    }
}

export default new BCE();